import { useMountEffect } from "@/hooks/use-mount-effect";
import { buildGlobalAnalyticsContext } from "@/services/analytics/build-global-analytics-context";
import { sessionTracking } from "@/services/analytics/session-tracking";
import { changeLanguage } from "@/services/i18n/i18n";
import { DEFAULT_LANGUAGE } from "@/services/i18n/i18n-config";
import { useState, useEffect, useRef } from "react";

import { configManager } from "../../services/config-manager";
import { warmVgImageCache } from "../../services/vg-image-cache/vg-image-cache";
import type { ClientConfig } from "../../types/client-config";
import { ClientConfigContext } from "./ClientConfigContext.shared";

/**
 * Client Configuration Provider
 *
 * Provides centralized client configuration management.
 * Config is loaded once from IndexedDB and shared across the app.
 */
export const ClientConfigProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  "use no memo";
  const [clientConfig, setClientConfig] = useState<ClientConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isInitialized = useRef(false);

  useMountEffect(() => {
    if (!isInitialized.current) {
      configManager.initialize();
      isInitialized.current = true;
    }
  });

  useMountEffect(() => {
    const loadConfig = async () => {
      try {
        setIsLoading(true);
        const config = await configManager.getConfig();
        setClientConfig(config);
        if (
          config?.golfCourseConfig?.mapProvider === "vg-image" &&
          config.golfCourseConfig.vgImageMap?.imageUrl
        ) {
          void warmVgImageCache(config.golfCourseConfig.vgImageMap.imageUrl);
        }
      } catch (error) {
        console.error("Failed to load client config:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  });

  // Apply data-mode attribute whenever themeMode changes
  useEffect(() => {
    const mode = clientConfig?.themeMode ?? "dark";
    document.documentElement.setAttribute("data-mode", mode);
  }, [clientConfig?.themeMode]);

  // Apply persisted language whenever it changes
  useEffect(() => {
    const language = clientConfig?.language ?? DEFAULT_LANGUAGE;
    void changeLanguage(language).catch((error) => {
      console.error("Failed to change language:", error);
    });
  }, [clientConfig?.language]);

  // Keep analytics context (course, device, country, consent) current for
  // every tracked event, however clientConfig changes (initial load,
  // updateConfig, refreshConfig, clearConfig). Idempotent per render, so
  // no effect is needed (see no-use-effect Rule 1).
  sessionTracking.setGlobalContext(buildGlobalAnalyticsContext(clientConfig));

  const updateConfig = async (config: ClientConfig) => {
    try {
      await configManager.setConfig(config);
      setClientConfig(config);
    } catch (error) {
      console.error("Failed to update client config:", error);
      throw error;
    }
  };

  const clearConfig = async () => {
    try {
      await configManager.clearConfig();
      setClientConfig(null);
    } catch (error) {
      console.error("Failed to clear client config:", error);
      throw error;
    }
  };

  const refreshConfig = async () => {
    try {
      const config = await configManager.getConfig();
      setClientConfig(config);
    } catch (error) {
      console.error("Failed to refresh client config:", error);
      throw error;
    }
  };

  return (
    <ClientConfigContext.Provider
      value={{
        clientConfig,
        isLoading,
        updateConfig,
        clearConfig,
        refreshConfig,
      }}
    >
      {children}
    </ClientConfigContext.Provider>
  );
};
